/**
 * 日期时间格式化工具
 */

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { EMPTY_PLACEHOLDER } from './constants';

/** 标记 relativeTime 插件是否已加载 */
let pluginInitialized = false;

/** 默认日期格式 */
export const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD';

/** 默认日期时间格式 */
export const DEFAULT_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

/**
 * 确保 dayjs 插件已加载（惰性初始化，避免模块级副作用）
 */
function ensurePlugins(): void {
  if (!pluginInitialized) {
    dayjs.extend(relativeTime);
    pluginInitialized = true;
  }
}

/**
 * 初始化格式化工具的 locale 设置
 *
 * 在应用启动时调用一次，设置 dayjs 的全局 locale。
 * 如果不调用，dayjs 将使用默认的 'en' locale。
 *
 * @param locale - dayjs locale 名称，如 'zh-cn'
 */
export function initFormatters(locale: string): void {
  ensurePlugins();
  dayjs.locale(locale);
}

/**
 * 格式化日期
 *
 * @param date - 日期值 (字符串/数字/Date/dayjs)
 * @param format - 格式模板，默认 'YYYY-MM-DD'
 * @returns 格式化后的日期字符串，无效日期返回 '--'
 */
export function formatDate(
  date: string | number | Date | dayjs.Dayjs | null | undefined,
  format: string = DEFAULT_DATE_FORMAT,
): string {
  if (!date) return EMPTY_PLACEHOLDER;
  const d = dayjs(date);
  return d.isValid() ? d.format(format) : EMPTY_PLACEHOLDER;
}

/**
 * 格式化日期时间
 *
 * @param date - 日期值
 * @param format - 格式模板，默认 'YYYY-MM-DD HH:mm:ss'
 * @returns 格式化后的日期时间字符串
 */
export function formatDateTime(
  date: string | number | Date | dayjs.Dayjs | null | undefined,
  format: string = DEFAULT_DATETIME_FORMAT,
): string {
  return formatDate(date, format);
}

/**
 * 格式化为相对时间 (如 "5分钟前"、"3天前")
 *
 * 30天以内显示相对时间，超过30天显示具体日期。
 *
 * @param date - 日期值
 * @param thresholdDays - 超过此天数显示具体日期，默认 30
 * @returns 相对时间字符串
 */
export function formatRelativeTime(
  date: string | number | Date | dayjs.Dayjs | null | undefined,
  thresholdDays: number = 30,
): string {
  if (!date) return EMPTY_PLACEHOLDER;
  ensurePlugins();
  const d = dayjs(date);
  if (!d.isValid()) return EMPTY_PLACEHOLDER;

  const diffDays = dayjs().diff(d, 'day');
  if (diffDays < thresholdDays) {
    return d.fromNow();
  }
  return d.format(DEFAULT_DATE_FORMAT);
}
